import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import axios from "axios";
import { API_BASE_URL } from "../constants";
import { useClub } from "./ClubContext";
import {
  FormationTag,
  SchemeFormation,
  OffensivePlayTag,
  OffensivePlayConcept,
  OffensiveRunConcept,
  OffensivePassConcept,
  OffensivePassProtectionConcept,
  OffensiveSituation,
  OffensiveGamePlan,
  OffensivePlaybookPlay,
} from "../types/SchemesTypes";

import { Game } from "../types/ScheduleTypes";
import { PersonnelGroup, Side } from "../types/DepthChartTypes";

// Define the shape of the context
interface SchemesContextType {
  // Data states
  isLoading: boolean;
  error: string | null;
  formations: SchemeFormation[];
  formationTags: FormationTag[];
  offensivePlayTags: OffensivePlayTag[];
  playConcepts: OffensivePlayConcept[];
  runConcepts: OffensiveRunConcept[];
  passConcepts: OffensivePassConcept[];
  passProtectionConcepts: OffensivePassProtectionConcept[];
  personnel: PersonnelGroup[];
  offensivePersonnel: PersonnelGroup[];
  defensivePersonnel: PersonnelGroup[];
  offensivePlaybookPlays: OffensivePlaybookPlay[];
  offensiveSituations: OffensiveSituation[];
  offensiveGamePlans: OffensiveGamePlan[];

  // Data fetching methods
  fetchFormations: () => Promise<void>;
  fetchTags: () => Promise<void>;
  fetchFormationTags: () => Promise<void>;
  fetchPlayTags: () => Promise<void>;
  fetchPlayConcepts: () => Promise<void>;
  fetchRunConcepts: () => Promise<void>;
  fetchPassConcepts: () => Promise<void>;
  fetchPassProtectionConcepts: () => Promise<void>;
  fetchPersonnel: () => Promise<void>;
  fetchOffensiveGamePlans: () => Promise<void>;

  // Data mutation methods
  saveFormation: (formation: SchemeFormation) => Promise<any>;
  deleteFormation: (id: number) => Promise<boolean>;
  saveFormationTag: (formationTag: {
    id?: number;
    name: string;
    description: string | null;
  }) => Promise<any>;
  deleteFormationTag: (id: number) => Promise<boolean>;
  savePlayTag: (playTag: {
    id?: number;
    name: string;
    description: string | null;
  }) => Promise<any>;
  deletePlayTag: (id: number) => Promise<boolean>;
  savePlayConcept: (concept: OffensivePlayConcept) => Promise<any>;
  deletePlayConcept: (id: number) => Promise<boolean>;
  saveRunConcept: (concept: OffensiveRunConcept) => Promise<any>;
  deleteRunConcept: (id: number) => Promise<boolean>;
  savePassConcept: (concept: OffensivePassConcept) => Promise<any>;
  deletePassConcept: (id: number) => Promise<boolean>;
  savePassProtectionConcept: (
    concept: OffensivePassProtectionConcept
  ) => Promise<any>;
  deletePassProtectionConcept: (id: number) => Promise<boolean>;
  savePersonnelGroup: (personnelGroup: {
    id?: number;
    name: string;
    side: Side;
  }) => Promise<any>;
  deletePersonnelGroup: (id: number) => Promise<boolean>;
  generateOffensivePlaybookPlays: () => void;
  generateGameplanPlays: (plays: OffensivePlaybookPlay[]) => OffensivePlaybookPlay[];
  saveOffensiveGamePlan: (gameplan: {
    name: string;
    description: string;
    game: Game;
    plays: OffensivePlaybookPlay[];
  }) => Promise<any>;
  updateOffensiveGamePlan: (gamePlan: OffensiveGamePlan) => Promise<any>;
  deleteOffensiveGamePlan: (id: number) => Promise<boolean>;
}

// Create the context with a default value
const SchemesContext = createContext<SchemesContextType | undefined>(undefined);

// Provider component
export const SchemesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { club } = useClub();

  // Data states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formations, setFormations] = useState<SchemeFormation[]>([]);
  const [formationTags, setFormationTags] = useState<FormationTag[]>([]);
  const [offensivePlayTags, setOffensivePlayTags] = useState<
    OffensivePlayTag[]
  >([]);
  const [playConcepts, setPlayConcepts] = useState<OffensivePlayConcept[]>([]);
  const [runConcepts, setRunConcepts] = useState<OffensiveRunConcept[]>([]);
  const [passConcepts, setPassConcepts] = useState<OffensivePassConcept[]>([]);
  const [passProtectionConcepts, setPassProtectionConcepts] = useState<
    OffensivePassProtectionConcept[]
  >([]);
  const [personnel, setPersonnel] = useState<PersonnelGroup[]>([]);
  const [offensivePersonnel, setOffensivePersonnel] = useState<
    PersonnelGroup[]
  >([]);
  const [defensivePersonnel, setDefensivePersonnel] = useState<
    PersonnelGroup[]
  >([]);
  const [offensivePlaybookPlays, setOffensivePlaybookPlays] = useState<
    OffensivePlaybookPlay[]
  >([]);
  const [offensiveSituations, setOffensiveSituations] = useState<
    OffensiveSituation[]
  >([]);
  const [offensiveGamePlans, setOffensiveGamePlans] = useState<
    OffensiveGamePlan[]
  >([]);

  // Fetch data on initial load
  useEffect(() => {
    fetchFormations();
    fetchTags();
    fetchFormationTags();
    fetchPlayTags();
    fetchPlayConcepts();
    fetchRunConcepts();
    fetchPassConcepts();
    fetchPassProtectionConcepts();
    fetchPersonnel();
    fetchOffensiveSituations();
    fetchOffensiveGamePlans();
  }, []);

  useEffect(() => {
    if (runConcepts.length > 0 || passConcepts.length > 0) {
      generateOffensivePlaybookPlays();
    }
  }, [runConcepts, passConcepts]);

  // Fetch formations
  const fetchFormations = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/formations/`);
      setFormations(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching formations:", err);
      setError(err.message || "Error fetching formations");
      setIsLoading(false);
    }
  };

  // Fetch personnel groups
  const fetchPersonnel = async () => {
    setIsLoading(true);
    await axios
      .get(`${API_BASE_URL}/personnel-groups/`)
      .then((response) => {
        setPersonnel(response.data);
        setOffensivePersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "OFFENSE"
          )
        );
        setDefensivePersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "DEFENSE"
          )
        );
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching personnel groups:", err);
        setError(err.message || "Error fetching personnel groups");
        setIsLoading(false);
      });
  };

  // Save or update formation
  const saveFormation = async (formation: SchemeFormation) => {
    await axios
      .post(`${API_BASE_URL}/formations/`, formation)
      .then((response) => {
        setIsLoading(false);
        setFormations([...formations, response.data]);
      })
      .catch((err: any) => {
        console.error("Error saving formation:", err);
        setError(err.message || "Error saving formation");
        setIsLoading(false);
      });
  };

  // Delete formation
  const deleteFormation = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/formations/${id}/`);
      setIsLoading(false);
      await fetchFormations();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error deleting formation"
      );
      console.error("Error deleting formation:", err);
      return false;
    }
  };

  // Fetch offensive play tags
  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/play-tags/`);
      setOffensivePlayTags(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching offensive play tags:", err);
      setError(err.message || "Error fetching tags");
      setIsLoading(false);
    }
  };

  // Fetch formation tags
  const fetchFormationTags = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/formation-tags/`);
      setFormationTags(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching formation tags:", err);
      setError(err.message || "Error fetching formation tags");
      setIsLoading(false);
    }
  };

  // Fetch play tags
  const fetchPlayTags = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/play-tags/`);
      setOffensivePlayTags(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching play tags:", err);
      setError(err.message || "Error fetching play tags");
      setIsLoading(false);
    }
  };

  // Fetch play concepts
  const fetchPlayConcepts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/offensive-play-concepts/`
      );
      setPlayConcepts(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching play concepts:", err);
      setError(err.message || "Error fetching play concepts");
      setIsLoading(false);
    }
  };

  const fetchRunConcepts = async () => {
    setIsLoading(true);
    await axios
      .get(`${API_BASE_URL}/offensive-run-concepts/`)
      .then((response) => {
        setRunConcepts(response.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching run concepts:", err);
        setError(err.message || "Error fetching run concepts");
        setIsLoading(false);
      });
  };

  const fetchPassConcepts = async () => {
    setIsLoading(true);
    await axios
      .get(`${API_BASE_URL}/offensive-pass-concepts/`)
      .then((response) => {
        setPassConcepts(response.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching pass concepts:", err);
        setError(err.message || "Error fetching pass concepts");
        setIsLoading(false);
      });
  };

  // Fetch pass protection concepts
  const fetchPassProtectionConcepts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/offensive-pass-protection-concepts/`
      );
      setPassProtectionConcepts(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching pass protection concepts:", err);
      setError(err.message || "Error fetching pass protection concepts");
      setIsLoading(false);
    }
  };

  // Save or update personnel group
  const savePersonnelGroup = async (personnelGroup: {
    id?: number;
    name: string;
    side: Side;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...personnelGroup,
        club: club?.id,
      };

      let response;
      if (personnelGroup.id) {
        response = await axios.put(
          `${API_BASE_URL}/personnel-groups/${personnelGroup.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/personnel-groups/`, data);
      }

      setIsLoading(false);
      await fetchPersonnel();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving personnel group"
      );
      console.error("Error saving personnel group:", err);
      throw err;
    }
  };

  // Delete personnel group
  const deletePersonnelGroup = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/personnel-groups/${id}/`);
      setIsLoading(false);
      await fetchPersonnel();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting personnel group"
      );
      console.error("Error deleting personnel group:", err);
      return false;
    }
  };

  // Save or update formation tag
  const saveFormationTag = async (formationTag: {
    id?: number;
    name: string;
    description: string | null;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...formationTag,
        club: club?.id,
      };

      let response;
      if (formationTag.id) {
        response = await axios.put(
          `${API_BASE_URL}/formation-tags/${formationTag.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/formation-tags/`, data);
      }

      setIsLoading(false);
      await fetchFormationTags();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving formation tag"
      );
      console.error("Error saving formation tag:", err);
      throw err;
    }
  };

  // Delete formation tag
  const deleteFormationTag = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/formation-tags/${id}/`);
      setIsLoading(false);
      await fetchFormationTags();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting formation tag"
      );
      console.error("Error deleting formation tag:", err);
      return false;
    }
  };

  // Save or update play tag
  const savePlayTag = async (playTag: {
    id?: number;
    name: string;
    description: string | null;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...playTag,
        club: club?.id,
      };

      let response;
      if (playTag.id) {
        response = await axios.put(
          `${API_BASE_URL}/play-tags/${playTag.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/play-tags/`, data);
      }

      setIsLoading(false);
      await fetchPlayTags();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error saving play tag"
      );
      console.error("Error saving play tag:", err);
      throw err;
    }
  };

  // Delete play tag
  const deletePlayTag = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/play-tags/${id}/`);
      setIsLoading(false);
      await fetchPlayTags();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error deleting play tag"
      );
      console.error("Error deleting play tag:", err);
      return false;
    }
  };

  // Save or update play concept
  const savePlayConcept = async (concept: OffensivePlayConcept) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...concept,
        club: concept.club || club?.id,
      };

      let response;
      if (concept.id) {
        response = await axios.put(
          `${API_BASE_URL}/offensive-play-concepts/${concept.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/offensive-play-concepts/`,
          data
        );
      }

      setIsLoading(false);
      await fetchPlayConcepts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving play concept"
      );
      console.error("Error saving play concept:", err);
      throw err;
    }
  };

  // Delete play concept
  const deletePlayConcept = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/offensive-play-concepts/${id}/`);
      setIsLoading(false);
      await fetchPlayConcepts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting play concept"
      );
      console.error("Error deleting play concept:", err);
      return false;
    }
  };

  // Save or update run concept
  const saveRunConcept = async (concept: OffensiveRunConcept) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...concept,
        club: concept.club || club?.id,
      };

      let response;
      if (concept.id) {
        response = await axios.put(
          `${API_BASE_URL}/offensive-run-concepts/${concept.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/offensive-run-concepts/`,
          data
        );
      }

      setIsLoading(false);
      await fetchRunConcepts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error saving run concept"
      );
      console.error("Error saving run concept:", err);
      throw err;
    }
  };

  // Delete run concept
  const deleteRunConcept = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/offensive-run-concepts/${id}/`);
      setIsLoading(false);
      await fetchRunConcepts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting run concept"
      );
      console.error("Error deleting run concept:", err);
      return false;
    }
  };

  // Save or update pass concept
  const savePassConcept = async (concept: OffensivePassConcept) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...concept,
        club: concept.club || club?.id,
      };

      let response;
      if (concept.id) {
        response = await axios.put(
          `${API_BASE_URL}/offensive-pass-concepts/${concept.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/offensive-pass-concepts/`,
          data
        );
      }

      setIsLoading(false);
      await fetchPassConcepts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving pass concept"
      );
      console.error("Error saving pass concept:", err);
      throw err;
    }
  };

  // Delete pass concept
  const deletePassConcept = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/offensive-pass-concepts/${id}/`);
      setIsLoading(false);
      await fetchPassConcepts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting pass concept"
      );
      console.error("Error deleting pass concept:", err);
      return false;
    }
  };

  // Save or update pass protection concept
  const savePassProtectionConcept = async (
    concept: OffensivePassProtectionConcept
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...concept,
        club: concept.club || club?.id,
      };

      let response;
      if (concept.id) {
        response = await axios.put(
          `${API_BASE_URL}/offensive-pass-protection-concepts/${concept.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/offensive-pass-protection-concepts/`,
          data
        );
      }

      setIsLoading(false);
      await fetchPassProtectionConcepts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving pass protection concept"
      );
      console.error("Error saving pass protection concept:", err);
      throw err;
    }
  };

  // Delete pass protection concept
  const deletePassProtectionConcept = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(
        `${API_BASE_URL}/offensive-pass-protection-concepts/${id}/`
      );
      setIsLoading(false);
      await fetchPassProtectionConcepts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting pass protection concept"
      );
      console.error("Error deleting pass protection concept:", err);
      return false;
    }
  };

  const fetchOffensiveSituations = async () => {
    await axios
      .get(`${API_BASE_URL}/offensive-situations/`)
      .then((response) => {
        setOffensiveSituations(response.data);
      })
      .catch((err) => {
        console.error("Error fetching offensive situations:", err);
      });
  };

  const generateOffensivePlaybookPlays = () => {
    const generatedPlays: OffensivePlaybookPlay[] = [];

    // Process run concepts
    runConcepts.forEach((runConcept, index) => {
      runConcept.personnel_formations.forEach((personnelFormation, fIndex) => {
        const newPlay: OffensivePlaybookPlay = {
          called_pass: false,
          name: runConcept.name,
          code_word_left: runConcept.code_word_left,
          code_word_right: runConcept.code_word_right,
          code_word:
            `${runConcept.code_word_left} / ${runConcept.code_word_right}`.trim(),
          call: `${runConcept.call_left} / ${runConcept.call_right}`.trim(),
          // Keep the string format for backward compatibility with the DataGrid
          personnel_formation: `${personnelFormation.formation.name} (${personnelFormation.personnel_group.name})`,
          // Also provide the separated objects for new functionality
          personnel_group: {
            id: personnelFormation.personnel_group.id,
            name: personnelFormation.personnel_group.name,
          },
          formation: {
            id: personnelFormation.formation.id,
            name: personnelFormation.formation.name,
          },
          concept: runConcept.play_concept,
          run_concept: runConcept,
          tags: runConcept.play_tags,
          offensive_situations: runConcept.offensive_situations,
          strongside: runConcept.strongside,
        };

        generatedPlays.push(newPlay);
      });
    });

    passConcepts.forEach((passConcept, index) => {
      passConcept.personnel_formations.forEach((personnelFormation, fIndex) => {
        const newPlay: OffensivePlaybookPlay = {
          called_pass: true,
          name: passConcept.name,
          code_word: `${passConcept.name}`,
          code_word_left: passConcept.name, // For pass concepts, use the same name for both
          code_word_right: passConcept.name, // For pass concepts, use the same name for both
          call: `${passConcept.name}`,
          // Keep the string format for backward compatibility with the DataGrid
          personnel_formation: `${personnelFormation.formation.name} (${personnelFormation.personnel_group.name})`,
          // Also provide the separated objects for new functionality
          personnel_group: {
            id: personnelFormation.personnel_group.id,
            name: personnelFormation.personnel_group.name,
          },
          formation: {
            id: personnelFormation.formation.id,
            name: personnelFormation.formation.name,
          },
          concept: passConcept.play_concept,
          pass_concept: passConcept,
          offensive_situations: passConcept.offensive_situations,
          pass_protection_concepts: passConcept.pass_protection_concepts,
        };
        generatedPlays.push(newPlay);
      });
    });

    // Update the state with generated plays
    setOffensivePlaybookPlays(generatedPlays);
  };

  const fetchOffensiveGamePlans = async () => {
    setIsLoading(true);
    setError(null);
    await axios
    .get(`${API_BASE_URL}/offensive-game-plans/`)
    .then((response)  => {
      setOffensiveGamePlans(response.data);
    })
    .catch((err) => {
      console.error("Error fetching offensive game plans:", err);
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  // Save offensive game plan
  const saveOffensiveGamePlan = async (gameplan: {
    name: string;
    description: string;
    game: Game;
    plays: OffensivePlaybookPlay[];
  }) => {
    setIsLoading(true);
    setError(null);
    try {

      // Prepare the data for the API
      const gameplanData = {
        name: gameplan.name,
        description: gameplan.description,
        club: club?.id,
        game: gameplan.game.id,
        offensive_plays: gameplan.plays,
      };

      // Send to the backend
      const response = await axios.post(
        `${API_BASE_URL}/offensive-game-plans/`,
        gameplanData
      );

      // Refresh the game plans list
      await fetchOffensiveGamePlans();

      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving offensive game plan"
      );
      console.error("Error saving offensive game plan:", err);
      throw err;
    }
  };

  // Add this function after saveOffensiveGamePlan
  const updateOffensiveGamePlan = async (gamePlan: OffensiveGamePlan) => {
    setIsLoading(true);
    setError(null);
    try {
      // Update the game plan via API
      const response = await axios.put(
        `${API_BASE_URL}/offensive-game-plans/${gamePlan.id}/`,
        gamePlan
      );

      // Refresh the list of game plans
      await fetchOffensiveGamePlans();

      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error updating offensive game plan"
      );
      console.error("Error updating offensive game plan:", err);
      throw err;
    }
  };

  const deleteOffensiveGamePlan = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/offensive-game-plans/${id}/`);
      await fetchOffensiveGamePlans();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      console.error("Error deleting offensive game plan:", err);
      setIsLoading(false);
      return false;
    }
  };

  // Add this function after generateOffensivePlaybookPlays
  const generateGameplanPlays = (plays: OffensivePlaybookPlay[]): OffensivePlaybookPlay[] => {
    const generatedPlays: OffensivePlaybookPlay[] = [];

    // Process each play
    plays.forEach(play => {
      // Create variations for each direction
      const directions = ["Left", "Right"];
      
      // For each direction, create a play
      directions.forEach(direction => {
        // Determine code words to use based on direction and strongside
        let codeWords: string[] = [];
        
        // Only apply strongside logic for runs
        if (!play.called_pass) {
          if (play.strongside === undefined) {
            // Use the generic code_word if strongside is undefined
            codeWords = [play.code_word || ""];
          } else if (play.strongside === null) {
            // For strongside === null (both directions), use both code words
            if (play.code_word_left) codeWords.push(play.code_word_left);
            if (play.code_word_right) codeWords.push(play.code_word_right);
            // If neither is defined, just use the generic code_word
            if (codeWords.length === 0) codeWords = [play.code_word || ""];
          } else {
            // For strongside === true or false, determine the appropriate code word
            const isStrongSide = play.strongside;
            const isRightDirection = direction === "Right";
            
            // If (strong + right) or (!strong + left) use right code word
            // Otherwise use left code word
            if ((isStrongSide && isRightDirection) || (!isStrongSide && !isRightDirection)) {
              codeWords = [play.code_word_right || ""];
            } else {
              codeWords = [play.code_word_left || ""];
            }
          }
        } else {
          // For pass plays, just use the regular code word
          codeWords = [play.code_word || ""];
        }
        
        // Create a play for each code word
        codeWords.forEach(codeWord => {
          generatedPlays.push({
            ...play,
            formation_direction: direction,
            code_word: codeWord,
          });
        });
      });
    });
    
    return generatedPlays;
  };

  // Combine all methods and state into the context value
  const contextValue: SchemesContextType = {
    // Data states
    isLoading,
    error,
    formations,
    personnel,
    offensivePlayTags,
    formationTags,
    playConcepts,
    runConcepts,
    passConcepts,
    passProtectionConcepts,
    offensivePersonnel,
    defensivePersonnel,
    offensivePlaybookPlays,
    offensiveSituations,
    offensiveGamePlans,
    // Data fetching methods
    fetchFormations,
    fetchTags,
    fetchFormationTags,
    fetchPlayTags,
    fetchPlayConcepts,
    fetchRunConcepts,
    fetchPassConcepts,
    fetchPassProtectionConcepts,
    fetchPersonnel,
    fetchOffensiveGamePlans,

    // Data mutation methods
    saveFormation,
    deleteFormation,
    savePersonnelGroup,
    deletePersonnelGroup,
    saveFormationTag,
    deleteFormationTag,
    savePlayTag,
    deletePlayTag,
    savePlayConcept,
    deletePlayConcept,
    saveRunConcept,
    deleteRunConcept,
    savePassConcept,
    deletePassConcept,
    savePassProtectionConcept,
    deletePassProtectionConcept,
    generateOffensivePlaybookPlays,
    generateGameplanPlays,
    saveOffensiveGamePlan,
    updateOffensiveGamePlan,
    deleteOffensiveGamePlan,
  };

  return (
    <SchemesContext.Provider value={contextValue}>
      {children}
    </SchemesContext.Provider>
  );
};

// Custom hook to use the schemes context
export const useSchemes = () => {
  const context = useContext(SchemesContext);
  if (context === undefined) {
    throw new Error("useSchemes must be used within a SchemesProvider");
  }
  return context;
};
